import React from "react";
import Immutable from "immutable";
import * as TargetsAPI from "js/oneview/targets/targets-api";
import eventBus from "js/cube19.event-bus";
import {formatDate} from "js/common/utils/time";
import {parseTargetDates} from "js/oneview/targets/utils";

const targetSource = "ClickThrough";

export const useTargetsEditor = (kpiId, targets, setTargets) => {
  const [targetsViewType, setTargetsViewType] = React.useState("SIMPLE");
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [updateError, setUpdateError] = React.useState(null);
  const [errorsByTargetId, setErrorsByTargetId] = React.useState(Immutable.Map());
  const [deletedTargets, setDeletedTargets] = React.useState(Immutable.List());

  const updateTarget = React.useCallback((target) => {
    setHasUnsavedChanges(true);

    if (target.get("deleted")) {
      setDeletedTargets(deleted => deleted.push(target));
      setTargets(currentTargets => {
        const targetIndex = currentTargets.findIndex(t => t.get("id") === target.get("id"));
        return currentTargets.delete(targetIndex);
      });
    } else {
      setTargets(currentTargets => {
        const targetIndex = currentTargets.findIndex(t => t.get("id") === target.get("id"));
        return currentTargets.set(targetIndex, target);
      });
    }
  }, [setTargets]);


  const saveTargets = React.useCallback(() => {
    const invalidTargets = targets.filter(target => {
      const targetValue = target.getIn(["value", "value"]);
      const targetStartDate = target.get("targetStart");
      const targetEndDate = target.get("targetEnd");
      return isNaN(targetValue) || targetValue < 0 || !targetStartDate.isValid() || !targetEndDate.isValid();
    });

    if (!invalidTargets.isEmpty()) {
      return Promise.resolve();
    }

    setIsSaving(true);

    const allTargets = targets.concat(deletedTargets);
    const targetsForApi = allTargets
        .map(target => target
            .update("targetStart", formatDate)
            .update("targetEnd", formatDate)
            .updateIn(["value", "value"], val => val + "")
            .toJS());

    return TargetsAPI
        .updateTargets(kpiId, targetsForApi, targetSource)
        .then(updatedTargets => {
          const processedTargets = updatedTargets
              .filterNot(t => t.get("deleted"))
              .map(parseTargetDates)
              .sortBy(t => -t.get("targetStart"));

          setTargets(processedTargets);
          setDeletedTargets(Immutable.List());
          eventBus.trigger("kpi:targets-changed", kpiId);
          setHasUnsavedChanges(false);
          setTargetsViewType("SIMPLE");
        })
        .catch(error => {
          if (error?.responseJSON?.type === "INVALID_TARGETS") {
            const errors = Immutable
                .fromJS(error.responseJSON.data)
                .mapKeys(targetId => parseInt(targetId, 10));
            setErrorsByTargetId(errors);
            setUpdateError(null);
          } else {
            setErrorsByTargetId(Immutable.Map());
            setUpdateError("Unable to save changes");
          }
        })
        .finally(() => {
          setIsSaving(false);
        });
  }, [targets, deletedTargets, kpiId, setTargets]);

  const startEditing = React.useCallback(() => {
    setTargetsViewType("EDIT");
  }, []);

  const cancelEditing = React.useCallback((reloadTargets) => {
    setTargetsViewType("SIMPLE");
    setHasUnsavedChanges(false);
    setUpdateError(null);
    setErrorsByTargetId(Immutable.Map());
    setDeletedTargets(Immutable.List());
    return reloadTargets();
  }, []);


  return {
    targetsViewType,
    hasUnsavedChanges,
    isSaving,
    updateError,
    errorsByTargetId,
    updateTarget,
    saveTargets,
    startEditing,
    cancelEditing
  };
};
